import React from "react";
import { motion } from "framer-motion";
import {
  Microscope,
  BedDouble,
  Home,
  Wine,
  Leaf,
  ArrowRight,
  Coins,
  Building2,
  RefreshCw,
  Globe,
} from "lucide-react";

const PILLAR_ICONS = [Microscope, BedDouble, Home, Wine, Leaf];
const ENGINE_ICONS = [Coins, Building2, RefreshCw];

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});

const Kicker = ({ children }) => (
  <span className="sl-kicker" data-testid="slide-kicker">
    {children}
  </span>
);

const SectionHead = ({ num, title, testid }) => (
  <div className="sl-head">
    <span className="sl-num">{num}</span>
    <h2 className="sl-section" data-testid={testid}>
      {title}
    </h2>
  </div>
);

export default function SlideView({ slide, lang, active }) {
  const t = (obj) => (obj ? obj[lang] || obj.it : "");
  const L = slide.layout;

  // COVER --------------------------------------------------------------
  if (L === "cover") {
    return (
      <div className="sl sl-cover" data-testid="slide-cover">
        <img src={slide.image} alt="San Leone" className="sl-bg" />
        <div className="sl-cover-scrim" />
        <div className="sl-cover-content">
          <motion.div {...fade(0.1)}>
            <Kicker>{t(slide.kicker)}</Kicker>
          </motion.div>
          <motion.h1 className="sl-title" {...fade(0.25)}>
            {slide.title}
          </motion.h1>
          <motion.div className="sl-rule" {...fade(0.4)} />
          <motion.p className="sl-subtitle" {...fade(0.5)}>
            {t(slide.subtitle)}
          </motion.p>
          <motion.p className="sl-tagline" {...fade(0.65)}>
            {t(slide.tagline)}
          </motion.p>
        </div>
      </div>
    );
  }

  // SPLIT with image right / left -------------------------------------
  if (L === "split-right" || L === "split-left") {
    const imgRight = L === "split-right";
    const TextBlock = (
      <div className="sl-col sl-col-text">
        {slide.badge && (
          <span className="sl-badge" data-testid="slide-badge">
            {t(slide.badge)}
          </span>
        )}
        <SectionHead num={slide.num} title={t(slide.section)} testid="slide-section" />
        <p className="sl-body" data-testid="slide-body">
          {t(slide.body)}
        </p>
        {slide.highlights && (
          <ul className="sl-tags">
            {slide.highlights.map((h, i) => (
              <li key={i} className="sl-tag">
                {t(h)}
              </li>
            ))}
          </ul>
        )}
        {slide.callout && (
          <p className="sl-callout" data-testid="slide-callout">
            {t(slide.callout)}
          </p>
        )}
      </div>
    );
    const ImgBlock = (
      <div className="sl-col sl-col-img">
        <img src={slide.image} alt={t(slide.section)} className="sl-img" />
      </div>
    );
    return (
      <div
        className={`sl sl-split ${slide.badge ? "sl-split-compact" : ""}`}
        data-testid={`slide-${slide.id}`}
      >
        {imgRight ? (
          <>
            {TextBlock}
            {ImgBlock}
          </>
        ) : (
          <>
            {ImgBlock}
            {TextBlock}
          </>
        )}
      </div>
    );
  }

  // FULL OVERLAY (facts / constraints) --------------------------------
  if (L === "full-overlay") {
    return (
      <div className="sl sl-overlay" data-testid={`slide-${slide.id}`}>
        <img
          src={slide.image}
          alt={t(slide.section)}
          className={`sl-bg ${slide.desaturate ? "sl-desat" : ""}`}
        />
        <div className="sl-overlay-scrim" />
        <div className="sl-overlay-content">
          <SectionHead num={slide.num} title={t(slide.section)} testid="slide-section" />
          {slide.body && <p className="sl-body sl-body-light">{t(slide.body)}</p>}
          {slide.facts && (
            <div className="sl-facts">
              {slide.facts.map((f, i) => (
                <div key={i} className="sl-fact">
                  <span className="sl-fact-value">{f.value}</span>
                  <span className="sl-fact-label">{t(f)}</span>
                </div>
              ))}
            </div>
          )}
          {slide.constraints && (
            <ul className="sl-tags sl-tags-light">
              {slide.constraints.map((c, i) => (
                <li key={i} className="sl-tag">
                  {t(c)}
                </li>
              ))}
            </ul>
          )}
          {slide.footer && <p className="sl-overlay-footer">{t(slide.footer)}</p>}
        </div>
      </div>
    );
  }

  // SITE MAP ------------------------------------------------------------
  if (L === "sitemap") {
    return (
      <div className="sl sl-sitemap" data-testid={`slide-${slide.id}`}>
        <div className="sl-map-text">
          <SectionHead num={slide.num} title={t(slide.section)} testid="slide-section" />
          <p className="sl-body sl-map-body">{t(slide.body)}</p>
          <div className="sl-legend">
            {slide.legend.map((l, i) => (
              <div key={i} className="sl-legend-item" data-testid={`map-legend-${i}`}>
                <span className={`sl-legend-swatch sw-${l.swatch}`} />
                <span className="sl-legend-value">{l.value}</span>
                <span className="sl-legend-label">{t(l)}</span>
              </div>
            ))}
          </div>
          <div className="sl-scalebar" data-testid="map-scalebar">
            <div className="sl-scalebar-track">
              <motion.div
                className="sl-scalebar-mine"
                initial={{ width: 0 }}
                animate={{ width: "5.7%" }}
                transition={{ duration: 1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
            <span className="sl-scalebar-caption">{t(slide.scaleNote)}</span>
          </div>
        </div>
        <div className="sl-map-wrap">
          <img src={slide.image} alt={t(slide.section)} className="sl-map-img" />
          <span className="sl-map-chip sl-map-chip-park" data-testid="map-label-park">
            {t(slide.mapLabels.park)}
          </span>
          <motion.div
            className="sl-map-mark"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            data-testid="map-mine-marker"
          >
            <span className="sl-map-chip sl-map-chip-mine">{t(slide.mapLabels.mine)}</span>
          </motion.div>
          <a
            href={slide.earthLink}
            target="_blank"
            rel="noreferrer"
            className="sl-map-earth"
            data-testid="map-earth-link"
          >
            <Globe size={14} strokeWidth={1.8} />
            <span>{t(slide.earthLabel)}</span>
            <ArrowRight size={13} strokeWidth={1.8} />
          </a>
        </div>
      </div>
    );
  }

  // PILLARS -----------------------------------------------------------
  if (L === "pillars") {
    return (
      <div className="sl sl-pillars" data-testid={`slide-${slide.id}`}>
        <img src={slide.image} alt="" className="sl-pillars-bg" />
        <div className="sl-pillars-inner">
          <SectionHead num={slide.num} title={t(slide.section)} testid="slide-section" />
          <p className="sl-body sl-body-light sl-intro">{t(slide.intro)}</p>
          <div className="sl-pillars-grid">
            {slide.pillars.map((p, i) => {
              const Icon = PILLAR_ICONS[i] || Leaf;
              return (
                <div key={i} className="sl-pillar" data-testid={`pillar-${i}`}>
                  <div className="sl-pillar-icon">
                    <Icon size={22} strokeWidth={1.5} />
                  </div>
                  <span className="sl-pillar-num">{String(i + 1).padStart(2, "0")}</span>
                  <span className="sl-pillar-text">{t(p)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // SPLIT TWO IMAGES --------------------------------------------------
  if (L === "split-two-img") {
    return (
      <div className="sl sl-twoimg" data-testid={`slide-${slide.id}`}>
        <div className="sl-twoimg-text">
          <SectionHead num={slide.num} title={t(slide.section)} testid="slide-section" />
          <p className="sl-body">{t(slide.body)}</p>
        </div>
        <div className="sl-twoimg-stack">
          {slide.images.map((src, i) => (
            <img key={i} src={src} alt="" className="sl-twoimg-img" />
          ))}
        </div>
      </div>
    );
  }

  // DUAL / TRIPLE ENGINE ----------------------------------------------
  if (L === "dual-engine") {
    return (
      <div className="sl sl-engine" data-testid={`slide-${slide.id}`}>
        <SectionHead num={slide.num} title={t(slide.section)} testid="slide-section" />
        <div className="sl-engine-grid">
          {slide.engines.map((e, i) => {
            const Icon = ENGINE_ICONS[i] || Coins;
            return (
              <React.Fragment key={i}>
                <div className="sl-engine-card" data-testid={`engine-${i}`}>
                  <div className="sl-engine-icon">
                    <Icon size={26} strokeWidth={1.5} />
                  </div>
                  <span className="sl-engine-tag">{t(e.tag)}</span>
                  <h3 className="sl-engine-title">{t(e.title)}</h3>
                  <p className="sl-engine-body">{t(e.body)}</p>
                </div>
                {i < slide.engines.length - 1 && (
                  <div className="sl-engine-arrow">
                    <ArrowRight size={26} strokeWidth={1.5} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  }

  // NUMBERS TABLE -----------------------------------------------------
  if (L === "numbers") {
    const val = (v) => (typeof v === "string" ? v : t(v));
    const Row = ({ r, testid }) => (
      <div
        className={`sl-numrow ${r.primary ? "sl-numrow-primary" : ""}`}
        data-testid={testid}
      >
        <div className="sl-numrow-label">
          <span>{t(r)}</span>
          {r.note && <em className="sl-numrow-note">{t(r.note)}</em>}
        </div>
        <div className="sl-numrow-value">{val(r.value)}</div>
      </div>
    );
    return (
      <div className="sl sl-numbers" data-testid={`slide-${slide.id}`}>
        <SectionHead num={slide.num} title={t(slide.section)} testid="slide-section" />
        {slide.lead && (
          <div className="sl-numbers-lead" data-testid="num-lead">
            <Row r={slide.lead} testid="num-lead-row" />
          </div>
        )}
        <div className="sl-numbers-groups">
          {slide.groups.map((g, gi) => (
            <div key={gi} className="sl-numgroup" data-testid={`num-group-${gi}`}>
              <h3 className="sl-numgroup-title">{t(g.title)}</h3>
              {g.rows.map((r, i) => (
                <Row key={i} r={r} testid={`num-row-${gi}-${i}`} />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // CASH FLOW CHART -----------------------------------------------------
  if (L === "cashflow") {
    return (
      <div className="sl sl-cashflow" data-testid={`slide-${slide.id}`}>
        <SectionHead num={slide.num} title={t(slide.section)} testid="slide-section" />
        <p className="sl-body sl-cf-intro">{t(slide.intro)}</p>
        <div className="sl-cf-chart" data-testid="cashflow-chart">
          {slide.bars.map((b, i) => (
            <div key={i} className="sl-cf-col" data-testid={`cf-bar-${i}`}>
              <span className="sl-cf-value">{t(b.label)}</span>
              <div className="sl-cf-track">
                <motion.div
                  className="sl-cf-bar"
                  initial={{ height: 0 }}
                  animate={{ height: `${(b.value / slide.maxValue) * 100}%` }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>
              <span className="sl-cf-year">{t(b.year)}</span>
            </div>
          ))}
        </div>
        <div className="sl-cf-milestones">
          {slide.milestones.map((m, i) => (
            <div key={i} className="sl-cf-milestone" data-testid={`cf-milestone-${i}`}>
              <span className="sl-cf-mtime">{t(m.time)}</span>
              <span className="sl-cf-mtext">{m[lang] || m.it}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ROADMAP -----------------------------------------------------------
  if (L === "roadmap") {
    return (
      <div className="sl sl-roadmap" data-testid={`slide-${slide.id}`}>
        <SectionHead num={slide.num} title={t(slide.section)} testid="slide-section" />
        <div className="sl-timeline">
          <div className="sl-timeline-line" />
          {slide.phases.map((p, i) => (
            <div key={i} className="sl-tl-item" data-testid={`phase-${i}`}>
              <div className="sl-tl-dot" />
              <span className="sl-tl-phase">{t(p.phase)}</span>
              <span className="sl-tl-time">{t(p.time)}</span>
              <p className="sl-tl-body">{p[lang] || p.it}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ASK ---------------------------------------------------------------
  if (L === "ask") {
    return (
      <div className="sl sl-ask" data-testid={`slide-${slide.id}`}>
        <img src={slide.image} alt="" className="sl-bg sl-ask-bg" />
        <div className="sl-ask-scrim" />
        <div className="sl-ask-content">
          <SectionHead num={slide.num} title={t(slide.section)} testid="slide-section" />
          <div className="sl-ask-amount">{slide.amount}</div>
          <p className="sl-body sl-body-light sl-ask-body">{t(slide.body)}</p>
          <div className="sl-ask-tags">
            {slide.open.map((o, i) => (
              <span key={i} className="sl-ask-chip">
                {t(o)}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return null;
}
